import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AnsagerKontext, type Ansagen } from './ansagerKontext';

/**
 * Der Ansager: zwei Bereiche, die **immer** im Dokument stehen.
 *
 * Warum das noetig ist: Ein Vorleseprogramm sagt Aenderungen an einem
 * `aria-live`-Bereich an - nicht dessen Inhalt beim Einbau. Die Meldungen der
 * Anwendung werden aber erst *mit* ihrem Text eingehaengt: Vorher gibt es
 * keinen Kasten, dann steht er samt Inhalt da. Beides passiert im selben
 * Durchgang, und damit bleibt die erste Rueckmeldung nach dem Speichern
 * haeufig stumm - obwohl `aria-live` gesetzt ist.
 *
 * Gemeldet im Codex-Review zu PR #56, und der Einwand ist berechtigt: PR #56
 * hat die Rueckmeldungen vereinheitlicht und ihnen `aria-live` gegeben, aber
 * nicht dafuer gesorgt, dass der Bereich vorher existiert.
 *
 * Diese beiden Bereiche haengen ab dem ersten Aufbau der Anwendung im Baum und
 * sind leer. `Meldung` schiebt seinen Text hinein - das ist dann eine
 * **Aenderung** an einem bestehenden Bereich, und die wird angesagt.
 *
 * Zwei Bereiche, weil `assertive` und `polite` sich nicht mischen lassen:
 * Fehler unterbrechen, alles andere wartet auf die naechste Pause.
 */
/**
 * Wie lange der Text stehen bleibt, bevor der Bereich wieder geleert wird.
 *
 * Er muss lang genug stehen, dass ein Vorleseprogramm ihn aufnimmt, und kurz
 * genug, dass niemand beim Durchgehen der Seite auf eine laengst erledigte
 * Meldung stoesst. Zwei Sekunden sind der uebliche Kompromiss.
 */
export const ANSAGE_DAUER_MS = 2000;

export const AnsagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hoeflich, setHoeflich] = useState('');
  const [dringend, setDringend] = useState('');
  const uhren = useRef<{ hoeflich?: ReturnType<typeof setTimeout>; dringend?: ReturnType<typeof setTimeout> }>({});

  /**
   * Der Text wird nach kurzer Zeit wieder geleert. Zwei Gruende:
   *
   * 1. **Er bliebe sonst fuer immer stehen.** Wird die Meldung weggeklickt
   *    oder die Seite gewechselt, haengt der Text weiter im Dokument - wer
   *    die Seite mit einem Vorleseprogramm durchgeht, stoesst dann auf eine
   *    Rueckmeldung zu einem Vorgang, der laengst vorbei ist.
   * 2. **Derselbe Text zweimal waere sonst stumm.** React verwirft eine
   *    Zustandsaenderung auf denselben Wert; im Dokument aendert sich nichts,
   *    und nur Aenderungen werden angesagt. Wer zweimal hintereinander in
   *    dieselbe Falscheingabe laeuft, hoert beim zweiten Mal nichts. Nach dem
   *    Leeren ist derselbe Text wieder eine Aenderung.
   *
   * Beides gemeldet im Codex-Review zu PR #59.
   */
  const ansagen = useCallback<Ansagen>((text, istDringend) => {
    const schluessel = istDringend ? 'dringend' : 'hoeflich';
    const setzen = istDringend ? setDringend : setHoeflich;

    clearTimeout(uhren.current[schluessel]);

    // Erst leeren, den Text dann im **naechsten** Durchgang setzen. Beides
    // hintereinander im selben Aufruf brachte nichts: React fasst Aenderungen
    // eines Ereignisses zusammen, aus '' und Text wird wieder nur der Text -
    // fuer das Dokument also keine Aenderung, und damit keine Ansage.
    setzen('');
    uhren.current[schluessel] = setTimeout(() => {
      setzen(text);
      uhren.current[schluessel] = setTimeout(() => setzen(''), ANSAGE_DAUER_MS);
    }, 0);
  }, []);

  useEffect(() => () => {
    clearTimeout(uhren.current.hoeflich);
    clearTimeout(uhren.current.dringend);
  }, []);

  return (
    <AnsagerKontext.Provider value={ansagen}>
      {children}
      {/* Die Reihenfolge ist gleichgueltig, die Anwesenheit nicht: Diese
          beiden Absaetze stehen von Anfang an da und bleiben stehen. */}
      <p aria-live="polite" data-ansage="hoeflich" className="sr-only">{hoeflich}</p>
      <p aria-live="assertive" data-ansage="dringend" className="sr-only">{dringend}</p>
    </AnsagerKontext.Provider>
  );
};

export default AnsagerProvider;
